import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, apiError, TOKEN_KEY, isAdmin } from "@/lib/api";
import { Logo } from "@/components/Brand";
import { Field } from "@/components/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Apply() {
  const { user, setUser } = useApp();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  if (isAdmin(user)) return <Navigate to="/admin" replace />;
  if (user) return <Navigate to="/portal/apply" replace />;

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!f.name.trim()) errs.name = "Name is required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim())) errs.email = "Enter a valid email address";
    if (f.password.length < 8) errs.password = "Password must be at least 8 characters";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", f);
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      navigate("/portal/apply", { replace: true });
    } catch (err) {
      const { message, fields } = apiError(err);
      setErrors(Object.keys(fields).length ? fields : { form: message });
    } finally {
      setBusy(false);
    }
  };
  const inp = (k, props = {}) => (
    <Input id={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`h-11 bg-card ${errors[k] ? "border-destructive" : ""}`}
      data-testid={`signup-${k}-input`} {...props} />
  );

  return (
    <div className="flex min-h-[85vh] items-center justify-center px-5 py-12" data-testid="apply-signup-page">
      <form onSubmit={submit} noValidate className="fade-up w-full max-w-md space-y-6" data-testid="signup-form">
        <Logo mark className="w-14" />
        <div>
          <div className="eyebrow">Apply for a loan</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight">Create your account</h1>
          <p className="mt-2 text-sm text-muted-foreground">Takes about 5 minutes. Have a photo ID and a recent pay stub ready.</p>
        </div>
        <Field label="Full name" name="name" error={errors.name}>{inp("name", { autoComplete: "name" })}</Field>
        <Field label="Email" name="email" error={errors.email}>{inp("email", { type: "email", autoComplete: "email" })}</Field>
        <Field label="Password" name="password" error={errors.password} hint="At least 8 characters">{inp("password", { type: "password", autoComplete: "new-password" })}</Field>
        {errors.form && <p className="text-sm text-destructive" data-testid="signup-error">{errors.form}</p>}
        <Button type="submit" disabled={busy} className="h-11 w-full" data-testid="signup-submit-button">
          {busy ? <Loader2 className="animate-spin" /> : <>Continue to application <ArrowRight /></>}
        </Button>
        <p className="text-sm text-muted-foreground">Already have an account? <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="signup-signin-link">Sign in</Link></p>
      </form>
    </div>
  );
}
